import { ContactFieldValueInterface, ContactSlugEnum } from '@systemeio/types';
import { setDateWithTimezone, toISOStringWithoutTimezone } from '@systemeio/util';
import debounce from 'lodash.debounce';
import { ContactFilterDateConditionEnum, ContactSearchFieldEnum } from 'modules/contacts/enums/contact-enum';
import { ContactId } from 'modules/contacts/types/contact-interface';
import { getConditions } from 'modules/contacts/utils/get-conditions';
import React, { useCallback, useEffect, type JSX } from 'react';
import { InfoRow } from 'shared/components/info-row';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { ContactInfoCardTooltip } from 'shared/components/tooltip/contact-info-card-tooltip';
import { pageRoute } from 'shared/constants/routes.constants';
import { ContactCardInfoInterface } from 'shared/hooks/use-contact-info-rows';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { useTouchDevice } from 'shared/providers/touch-device-provider';
import { QueryKeysEnum } from 'shared/route-query/enum/query-keys-enum';
import { OrderEnum } from 'shared/types/pagination-interface';
import { formatExactDateTime } from 'shared/utils/date-time-format';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
import { ContactTooltipDataInterface, useContactsTooltipData } from '../hooks/use-contacts-tooltip-data';
interface ContactInfoTooltipProps {
  children?: JSX.Element | string;
  contactId?: ContactId;
  removedAt: string | null;
}
const getEmail = (fieldValues: ContactFieldValueInterface[]) => fieldValues.find(el => el.contactField.slug === ContactSlugEnum.Email)?.value;
const toContactCardInfo = ({
  fieldValues,
  referredBy
}: ContactTooltipDataInterface): ContactCardInfoInterface => ({
  email: getEmail(fieldValues) ?? '',
  fieldValues,
  referredBy: referredBy && {
    email: getEmail(referredBy.fieldValues)
  }
});

/* A removed contact has no page of its own: it links to the bin, filtered by its removal day. */
const getContactHref = (contactId: ContactId, removedAt: string | null) => {
  if (!removedAt) return getPageRoute(pageRoute.ContactDetails, {
    contactId
  });
  const removedOn = toISOStringWithoutTimezone(setDateWithTimezone(removedAt, 'UTC')).split('T')[0];
  const conditions = getConditions({
    filter: {
      [ContactSearchFieldEnum.removed_at]: [{
        compareOperation: ContactFilterDateConditionEnum.equals,
        searchValue: `${removedOn}T00:00:00`
      }]
    }
  });
  return `${getPageRoute(pageRoute.ContactsBin)}?${new URLSearchParams({
    [QueryKeysEnum.Order]: OrderEnum.prev,
    [QueryKeysEnum.Filter]: JSON.stringify(conditions)
  })}`;
};

/** The contact card for a contact the page knows only by id: it loads the contact once opened. */
const LazyContactInfoTooltip = ({
  children,
  contactId,
  removedAt
}: ContactInfoTooltipProps) => {
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  const isTouchDevice = useTouchDevice();
  const {
    contactData,
    fetchContactData,
    clearContactData
  } = useContactsTooltipData();
  const fetchContactDataWithDebounce = useCallback(debounce((id: ContactId) => {
    fetchContactData(id);
  }, 500), [fetchContactData]);
  useEffect(() => {
    return () => {
      fetchContactDataWithDebounce.cancel();
    };
  }, [fetchContactDataWithDebounce]);
  const handleMouseEnter = () => {
    if (contactId) {
      fetchContactDataWithDebounce(contactId);
    }
  };
  const handleMouseLeave = () => {
    fetchContactDataWithDebounce.cancel();
    clearContactData();
  };

  /*
   * No debounce on touch: the tap is a deliberate request, unlike a pointer passing over the row.
   * The contact is kept on close, so the card does not turn into a placeholder while the modal
   * animates out.
   */
  const handleModalOpenChange = (open: boolean) => {
    if (open && contactId) {
      fetchContactData(contactId);
    }
  };
  const href = contactId ? getContactHref(contactId, removedAt) : undefined;

  /*
   * Touch emulates `mouseenter` on the tap and `mouseleave` on the next tap elsewhere, which would
   * be a tap inside the modal and abort the contact it loads. The modal loads it itself.
   */
  return <span onMouseEnter={isTouchDevice ? undefined : handleMouseEnter} onMouseLeave={isTouchDevice ? undefined : handleMouseLeave}>
      <ContactInfoCardTooltip inline contact={contactData && toContactCardInfo(contactData)} extraRows={removedAt && <InfoRow label={t('global.contact_removed')} value={formatExactDateTime(removedAt, {
      intlLocale,
      timeZone: user?.timezone
    })} />} href={href} onModalOpenChange={handleModalOpenChange}>
        {!href ? children : <LinkWithoutPrefetch href={href} className="hover:underline">
            {children}
          </LinkWithoutPrefetch>}
      </ContactInfoCardTooltip>
    </span>;
};
export default LazyContactInfoTooltip;